import { Component, OnInit, ViewChild, ViewChildren, QueryList, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject } from 'rxjs';
import { ConfirmationService } from "primeng/api";
import { IBrand } from '../../shared/interfaces/brand';
import { IOffer } from '../../shared/interfaces/offer';
import { IOfferField } from '../../interfaces/offer-field';
import { ILayoutFieldKey }  from '../../interfaces/pass-layout/layout-field-key';
import { ITemplateLayoutSection } from '../../interfaces/pass-layout/template-layout-section';
import { IPlatformTemplateLayouts } from '../../interfaces/pass-layout/platforrm-template-layouts';
import { IAssignedSlots } from '../../interfaces/pass-layout/assigned-slots';
import { IPassLayoutContext } from '../../interfaces/pass-layout/pass-layout-context';
import { DigitalWalletPlatform } from '../../enums/digital-wallet-platform';
import { PassLayoutService } from '../../services/construct/pass-layout.service';
import { SharedService } from "../../shared/services/shared.service";
import { BrandProfileService} from '../../services/construct/brand-profile.service';
import { UserContextService } from '../../services/safeguard/user-context.service';
import { BrandAdminHeaderComponent } from '../../shared/components/brand-admin-header/brand-admin-header.component';
import { PassLayoutComponent } from '../../components/pass-layout/pass-layout.component';

@Component({
  selector: 'app-offer-pass-layouts',
  standalone: false,
  templateUrl: './offer-pass-layouts.component.html',
  styleUrl: './offer-pass-layouts.component.css',
})

export class OfferPassLayoutsComponent implements OnInit {

  @ViewChild(BrandAdminHeaderComponent, {static : true}) child! : BrandAdminHeaderComponent;  
  @ViewChildren(PassLayoutComponent) childComponents!: QueryList<PassLayoutComponent>;

  private router = inject(Router);
  private confirmationService = inject(ConfirmationService);
  private sharedService = inject(SharedService);
  private userContextService = inject(UserContextService);
  private passLayoutService = inject(PassLayoutService);
  private brandProfileService = inject(BrandProfileService);

  private awMessageSubject = new BehaviorSubject<any>(null);
  private gwMessageSubject = new BehaviorSubject<any>(null);
  private swMessageSubject = new BehaviorSubject<any>(null);

  awMessage$ = this.awMessageSubject.asObservable();  
  gwMessage$ = this.gwMessageSubject.asObservable();  
  swMessage$ = this.swMessageSubject.asObservable();  

  awLayoutContext: IPassLayoutContext = {
    platform: DigitalWalletPlatform.AW,
    layoutSections: [], 
    fieldKeys: [],
    assignedSlots: {}
  }

  gwLayoutContext: IPassLayoutContext = {
    platform: DigitalWalletPlatform.GW,
    layoutSections: [], 
    fieldKeys: [],
    assignedSlots: {}
  }

  swLayoutContext: IPassLayoutContext = {
    platform: DigitalWalletPlatform.SW,
    layoutSections: [], 
    fieldKeys: [],
    assignedSlots: {}
  }


  titleMessage = "Field";

  currentBrand!: IBrand;
  currentOffer!: IOffer;
  currentPassType!: string;

  platformTemplateLayouts!: IPlatformTemplateLayouts;  
  fieldKeys: ILayoutFieldKey[] = [];  
  assignedSlots!: IAssignedSlots;

  currentField!: IOfferField;
  offerFields!: IOfferField[];
  fieldDetails!: any;
  fieldDetailsCols: any[] = [];

  isSamsungPlatformDisabled: boolean = false;  
  isSubmitShown: boolean = false;  
  isDetailsShown: boolean = false;
  isFieldDetailsShown: boolean = false;

  tabIndex: string = 'Apple Wallet';

  constructor() {
  }  

  ngOnInit(): void {
    this.fieldDetailsCols = [
      { field: 'attrName', header: 'Attribute Name', width:'35%', display: 'table-cell' },
      { field: 'attrValue', header: 'Attribute Value', width:'65%', display: 'table-cell' }
    ]; 
    if (this.sharedService.isCurrentBrandKnown() && this.sharedService.isCurrenOfferKnown()) {
      this.currentBrand = this.sharedService.getCurrentBrand();
      this.currentOffer = this.sharedService.getCurrentOffer();
      this.currentPassType = this.sharedService.getCurrentPassType();
      this.child.makeTitleMessage(this.currentBrand, this.titleMessage, this.currentOffer);
      this.isSamsungPlatformDisabled = !this.currentBrand.isSamsungPlatformEnabled || false;
      this.getOfferFields(this.currentBrand.brandCode, this.currentOffer.offerId, this.currentBrand.baseUrl);
    }
    // Hide the submit button for users with read-only access type
    if (this.userContextService.userHasPrivilege('PRIV_WAL_BRAND_CONFIG_OFFER_FIELD_MODIFY')) {
      this.isSubmitShown = true;
    }

  }

  // Select all existing displayable offer fields
  getOfferFields(brandCode: string, offerId: number, brandUrl: string) {
    // Call the service to invoke a Web API call
    this.brandProfileService.getDisplayableOfferFields(brandCode, offerId, brandUrl)
    .subscribe({
      next: (response) => {
        if (response) {
          response.forEach(item => {
            this.offerFields = response;
            // if (this.displayableFieldTypes.includes(item.fieldType)) {
              this.fieldKeys.push({
                fieldKey: item.fieldKey, 
                fieldType: item.fieldType, 
                fieldOrder: item.fieldOrder,
                hasLink: item.offerlink || item.altOfferlink || item.shortLink || item.altShortLink ? true : false, 
                hasImage: item.imageUID ? true : false,
                inUse: false});
            // }
          })
          this.fieldKeys.sort((a, b) => a.fieldKey.localeCompare(b.fieldKey));
        }
        // Have the field keys ready to be passed to the child component
        this.awLayoutContext.fieldKeys = JSON.parse(JSON.stringify(this.fieldKeys));
        this.gwLayoutContext.fieldKeys = JSON.parse(JSON.stringify(this.fieldKeys));
        this.swLayoutContext.fieldKeys = JSON.parse(JSON.stringify(this.fieldKeys));

        // Retrieve template layouts for all three child components
        this.getPlatformTemplateLayouts(brandCode, this.currentPassType, offerId, brandUrl);
      },
      error: (error) => {
        this.alertTheUser("Error", error.message);
      },
      complete: () => {
      }
    });
  }   

  // Retrieve the metadata for each platform, including a list of sections with positioned slots
  getPlatformTemplateLayouts(brandCode: string, passType: string, offerId: number, baseUrl: string) {
    // Call the service to invoke a Web API call
    this.passLayoutService.getPlatformTemplateLayouts(brandCode, passType, baseUrl)
    .subscribe({
      next: (response) => {
        if (response) {
          this.platformTemplateLayouts = response;
          // console.log('getPlatformTemplateLayouts', response);

          // Have the template layouts ready to be passed to the child component
          this.awLayoutContext.layoutSections = this.platformTemplateLayouts.awLayout;
          this.gwLayoutContext.layoutSections = this.platformTemplateLayouts.gwLayout;
          this.swLayoutContext.layoutSections = this.platformTemplateLayouts.swLayout;

          // Retrieve assigned slots for all three components
          this.getAssignedSlots(brandCode, passType, offerId, baseUrl);
        }
      },
      error: (error) => {
              console.log('getSlotDescs - error', error);
      },
      complete: () => {
      }
    });

  }

  // Find out already assigned slots
  getAssignedSlots(brandCode: string, passType: string, offerId: number, baseUrl: string) {
    // Call the service to invoke a Web API call
    this.passLayoutService.getAssignedSlots(brandCode, passType, offerId, baseUrl)
    .subscribe({
      next: (response) => {
        if (response) {
          this.assignedSlots = response;
          // Have the template layouts ready to be passed to the child component
          this.awLayoutContext.assignedSlots = this.assignedSlots['awSlots'] ?? {};
          this.gwLayoutContext.assignedSlots = this.assignedSlots['gwSlots'] ?? {};
          this.swLayoutContext.assignedSlots = this.assignedSlots['swSlots'] ?? {};

          // Send the layout context to all three child components
          this.awMessageSubject.next(this.awLayoutContext);
          this.gwMessageSubject.next(this.gwLayoutContext);
          this.swMessageSubject.next(this.swLayoutContext);
        }
      },
      error: (error) => {
              console.log('getSlotDescs - error', error);
      },
      complete: () => {
      }
    });

  }
  
  // Not in use
  onActiveTabChange(event: any) {
  }

  // Redirect to back to brand offers page
  brandOfferRedirect(event: any) {
    this.router.navigate(['/brandOffers']);
  }

  // Collect all assigned slots with offer field keys, then submit them to the sserver
  onSubmit($event: any) {
    const assignedSlots: IAssignedSlots = {};
    this.childComponents.toArray().forEach( child => {
      const [platform, slots] = child?.getAssignedSlots();
      switch (platform) {
        case 'AW':
          assignedSlots['awSlots'] = slots;
          break;
        case 'GW':
          assignedSlots['gwSlots'] = slots;
          break;
        case 'SW':
          assignedSlots['swSlots'] = slots;
          break;
      }
    })
    console.log('submit assignedSlots', assignedSlots);
    // Invoke the service class to make a call into the endpoint
    this.passLayoutService.submitAssignedSlots(this.currentBrand.brandCode, this.currentPassType, 
      this.currentOffer.offerId, this.currentBrand.baseUrl, assignedSlots)
      .subscribe({
        next: (response) => {
          if (response) {
            this.alertTheUser('Processed', 'Successfully aasigned displayable fields within the positioned layout specification.');
          }
        },
        error: (error) => {
          this.alertTheUser("Error", error.message);
        },
        complete: () => {
        }
      });

  }

  // Simply go to Brand Offers page
  onCancel($event: any) {
    this.router.navigate(['/brandOffers']);
  }  

  alertTheUser(header: string, message: string) {
    this.confirmationService.confirm({
      message: message,
      header: header,
      key: 'offer-pass-layouts',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: "Ok",
      rejectVisible: false,
      acceptButtonStyleClass: "p-button-info wal-info  p-button-rounded",
      accept: () => {
         return;
       }
    });
  }

  // This receives a message from a child component to display/hide specific offer field details
  onNotifySignal = signal((isFieldDetailsShown: boolean, fieldKey: string) => {

    if (isFieldDetailsShown) {

      // this.currentField = this.offerFields.find(offerField => offerField.fieldKey === fieldKey) || this.offerFields[0];
 
      const found = this.offerFields.find(of => of.fieldKey === fieldKey) ?? this.offerFields[0];
      if (!found) {
        return;
      }
      this.currentField = found;

      this.fieldDetails = [
        {
          attrName: 'Field Key',
          attrValue: this.currentField.fieldKey
        },
        {
          attrName: 'Field Label',
          attrValue: this.currentField.fieldLabel
        },
        {
          attrName: 'Field Value',
          attrValue: this.currentField.fieldValue
        },
        {
          attrName: 'Field Type',
          attrValue: this.currentField.fieldType
        },
        {
          attrName: 'Link',
          attrValue: this.currentField.offerlink
        }
      ];
    }
    this.isFieldDetailsShown = isFieldDetailsShown && (this.currentField ? true : false);
      // console.log(`Child triggered event! Flag: ${isFieldDetailsShown}, Message: ${fieldKey}`);
  });


}


//     this.platformTemplateLayouts = 
// {
//     "awLayout": [
//         {
//             "sectionName": "Auxiliary",
//             "pageNum": 1,
//             "numOfCols": 1,
//             "slotList": [
//                 {
//                     "slotId": "aux1",
//                     "slotName": "Auxiliary 1"
//                 },
//                 {
//                     "slotId": "aux2",
//                     "slotName": "Auxiliary 2"
//                 }
//             ]
//         }
//     ],
//     "gwLayout": [
//         {
//             "sectionName": "Headers",
//             "pageNum": 1,
//             "numOfCols": 1,
//             "slotList": [
//                 {
//                     "slotId": "cardTitle",
//                     "slotName": "Card Title"
//                 },
//                 {
//                     "slotId": "header",
//                     "slotName": "Header"
//                 },
//                 {
//                     "slotId": "subHeader",
//                     "slotName": "Subheader"
//                 }
//             ]
//         },
//         {
//             "sectionName": "Grid",
//             "pageNum": 1,
//             "numOfCols": 2,
//             "slotList": [
//                 {
//                     "slotId": "r1c1",
//                     "slotName": "R1C1"
//                 },
//                 {
//                     "slotId": "r1c2",
//                     "slotName": "R1C2"
//                 },
//                 {
//                     "slotId": "r2c1",
//                     "slotName": "R2C1"
//                 },
//                 {
//                     "slotId": "r2c2",
//                     "slotName": "R2C2"
//                 },
//                 {
//                     "slotId": "r3c1",
//                     "slotName": "R3C1"
//                 },
//                 {
//                     "slotId": "r3c2",
//                     "slotName": "R3C2"
//                 },
//                 {
//                     "slotId": "r4c1",
//                     "slotName": "R4C1"
//                 },
//                 {
//                     "slotId": "r4c2",
//                     "slotName": "R4C2"
//                 }
//             ]
//         },
//         {
//             "sectionName": "Details",
//             "pageNum": 2,
//             "numOfCols": 1,
//             "slotList": [
//                 {
//                     "slotId": "r1",
//                     "slotName": "R1"
//                 },
//                 {
//                     "slotId": "r2",
//                     "slotName": "R2"
//                 },
//                 {
//                     "slotId": "r3",
//                     "slotName": "R3"
//                 },
//                 {
//                     "slotId": "r4",
//                     "slotName": "R4"
//                 },
//                 {
//                     "slotId": "r5",
//                     "slotName": "R5"
//                 },
//                 {
//                     "slotId": "r6",
//                     "slotName": "R6"
//                 },
//                 {
//                     "slotId": "r7",
//                     "slotName": "R7"
//                 },
//                 {
//                     "slotId": "r8",
//                     "slotName": "R8"
//                 },
//                 {
//                     "slotId": "r9",
//                     "slotName": "R9"
//                 },
//                 {
//                     "slotId": "r10",
//                     "slotName": "R10"
//                 }
//             ]
//         }
//     ],
//     "swLayout": [
//         {
//             "sectionName": "Headers",
//             "pageNum": 1,
//             "numOfCols": 1,
//             "slotList": [
//                 {
//                     "slotId": "title",
//                     "slotName": "Title"
//                 },
//                 {
//                     "slotId": "subtitle",
//                     "slotName": "Subtitle"
//                 }
//             ]
//         },
//         {
//             "sectionName": "Texts",
//             "pageNum": 1,
//             "numOfCols": 1,
//             "slotList": [
//                 {
//                     "slotId": "text1",
//                     "slotName": "Text 1"
//                 },
//                 {
//                     "slotId": "text2",
//                     "slotName": "Text 2"
//                 }
//             ]
//         },
//         {
//             "sectionName": "Links",
//             "pageNum": 1,
//             "numOfCols": 1,
//             "slotList": [
//                 {
//                     "slotId": "link1",
//                     "slotName": "Link 1"
//                 },
//                 {
//                     "slotId": "link2",
//                     "slotName": "Link 2"
//                 },
//                 {
//                     "slotId": "link3",
//                     "slotName": "Link 3"
//                 }
//             ]
//         }
//     ]
// }    


    // this.assignedSlots = {
    //   AW: {},
    //   GW: {      
    //       "header": "nameDisp",
    //       "r2c1": "duedateDisp",
    //       "r7": "Privacy"
    //     },
    //   SW: {}
    // }

      // displayableFieldTypes: string[] = 
  //   ['backFields', 'ssoField', 'headerFields','walPagesField','secondaryFields', 'auxiliaryFields','primaryFields'];

